// FIFA-rules football scoring and standings (see
// `features/football-tournaments.md`). The table-tennis best-of-N engine in
// `scoring.ts` / `standings.ts` is untouched; a football tournament routes
// through the parallel functions here, keyed off `TournamentState.sportId`.
//
// A football match reuses the persisted `games: GameScore[]` array with
// different meaning:
//   - group cell:    at most one entry — games[0] is the full-time score
//                    (pointsA/pointsB = goals). Draws are legal.
//   - bracket node:  games[0] is the score after regulation (+ extra time);
//                    when drawn, games[1] is the penalty shoot-out, which must
//                    have a winner. A knock-out match always yields a winner.
//
// Because a drawn score can be a live 1–1 or a final 1–1, completion cannot be
// derived from the score alone: the caller records an explicit `final` flag
// (see `deriveFootballStatus`).

import type {
  BracketNode,
  Cell,
  FootballCardEvent,
  FootballGoalEvent,
  FootballMatchEvent,
  FootballStanding,
  GameScore,
  MatchStatus,
  PlayerId,
} from './types';
import { TEAM_MEMBER_MAX } from './types';

/** League points for a group-stage win / draw (FIFA 3-1-0). */
export const FOOTBALL_WIN_POINTS = 3;
export const FOOTBALL_DRAW_POINTS = 1;

/**
 * Highest match minute an event may carry: 120 minutes of regulation plus
 * extra time, with slack for stoppage time recorded past the hour marks.
 */
export const FOOTBALL_MINUTE_MAX = 130;

/** Most yellow cards one squad member may collect in a single match. */
export const FOOTBALL_YELLOW_MAX = 2;
/** Most red cards one squad member may collect in a single match. */
export const FOOTBALL_RED_MAX = 1;

/** Which stage a football match belongs to; decides whether draws may stand. */
export type FootballPhase = 'group' | 'bracket';

export interface FootballMatchSummary {
  goalsA: number; // full-time goals (regulation + extra time)
  goalsB: number;
  hasShootout: boolean; // a penalty shoot-out entry is recorded
  shootoutA: number; // shoot-out conversions; 0 when no shoot-out
  shootoutB: number;
  /**
   * The winning side: the regulation winner, or the shoot-out winner after a
   * draw. Null while nothing is recorded, while a group match is drawn, or
   * while a drawn bracket match still awaits its shoot-out.
   */
  winner: 'A' | 'B' | null;
  played: boolean; // a full-time score is recorded
  drawn: boolean; // the full-time score is level
}

/** Aggregates a football match's entries into goals, shoot-out and winner. */
export function summarizeFootballMatch(
  games: readonly GameScore[],
): FootballMatchSummary {
  const regulation = games[0];
  const shootout = games[1];
  const goalsA = regulation?.pointsA ?? 0;
  const goalsB = regulation?.pointsB ?? 0;
  const played = regulation !== undefined;
  const drawn = played && goalsA === goalsB;

  let winner: 'A' | 'B' | null = null;
  if (played && goalsA !== goalsB) {
    winner = goalsA > goalsB ? 'A' : 'B';
  } else if (drawn && shootout && shootout.pointsA !== shootout.pointsB) {
    winner = shootout.pointsA > shootout.pointsB ? 'A' : 'B';
  }

  return {
    goalsA,
    goalsB,
    hasShootout: shootout !== undefined,
    shootoutA: shootout?.pointsA ?? 0,
    shootoutB: shootout?.pointsB ?? 0,
    winner,
    played,
    drawn,
  };
}

/** Stable code identifying why a football score is invalid (for localised messages). */
export type FootballGamesInvalidCode =
  | 'INVALID_SCORE'
  | 'TOO_MANY_ENTRIES'
  | 'SHOOTOUT_WITHOUT_DRAW'
  | 'SHOOTOUT_TIE';

export interface FootballGamesValidation {
  valid: boolean;
  /** English description of the violation (server responses, logs). */
  reason?: string;
  /** Machine-readable violation code; the UI renders it via the catalogue. */
  code?: FootballGamesInvalidCode;
}

function isValidScore(value: number): boolean {
  return Number.isInteger(value) && value >= 0;
}

/**
 * Enforces the football score shape. Goals are non-negative integers; a group
 * match is a single score; a bracket match may add a shoot-out entry — but only
 * after a drawn full-time score, and the shoot-out itself cannot be level.
 */
export function validateFootballGames(
  games: readonly GameScore[],
  phase: FootballPhase,
): FootballGamesValidation {
  for (const game of games) {
    if (!isValidScore(game.pointsA) || !isValidScore(game.pointsB)) {
      return {
        valid: false,
        reason: 'Goals must be non-negative whole numbers.',
        code: 'INVALID_SCORE',
      };
    }
  }
  const maxEntries = phase === 'group' ? 1 : 2;
  if (games.length > maxEntries) {
    return {
      valid: false,
      reason:
        phase === 'group'
          ? 'A group match is a single score.'
          : 'A knock-out match is a score plus at most one shoot-out.',
      code: 'TOO_MANY_ENTRIES',
    };
  }
  if (games.length === 2) {
    if (games[0].pointsA !== games[0].pointsB) {
      return {
        valid: false,
        reason: 'A shoot-out can only follow a drawn match.',
        code: 'SHOOTOUT_WITHOUT_DRAW',
      };
    }
    if (games[1].pointsA === games[1].pointsB) {
      return {
        valid: false,
        reason: 'A penalty shoot-out must have a winner.',
        code: 'SHOOTOUT_TIE',
      };
    }
  }
  return { valid: true };
}

/**
 * Whether the recorded score is complete enough to be declared final: a group
 * match needs its full-time score (a draw stands); a knock-out match needs a
 * winner (via the shoot-out when drawn).
 */
export function isFootballResultComplete(
  games: readonly GameScore[],
  phase: FootballPhase,
): boolean {
  const summary = summarizeFootballMatch(games);
  if (!summary.played) return false;
  return phase === 'group' ? true : summary.winner !== null;
}

/**
 * Derived lifecycle status. Unlike the best-of engine, completion is not
 * derivable from the score (a drawn score can be live or final), so the
 * organiser's explicit `final` flag decides — callers must gate `final` on
 * `isFootballResultComplete` first.
 */
export function deriveFootballStatus(
  games: readonly GameScore[],
  final: boolean,
): MatchStatus {
  if (games.length === 0) return 'PENDING';
  return final ? 'COMPLETED' : 'IN_PROGRESS';
}

/** Winner of a completed football bracket node, or null (mirrors `decideWinner`). */
export function decideFootballWinner(node: BracketNode): PlayerId | null {
  if (node.status !== 'COMPLETED') return null;
  const { winner } = summarizeFootballMatch(node.games);
  if (winner === 'A') return node.playerA;
  if (winner === 'B') return node.playerB;
  return null;
}

function numericId(playerId: PlayerId): number {
  const match = /(\d+)/.exec(playerId);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

/**
 * FIFA tie-break comparator: Points -> Goal difference -> Goals scored, with
 * the team number as the deterministic stand-in for drawing of lots. The
 * head-to-head criterion between two otherwise-tied teams is applied as a
 * post-pass in `computeFootballStandings` (a pairwise comparator cannot see it).
 */
export function compareFootballStandings(
  a: FootballStanding,
  b: FootballStanding,
): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalDiff !== a.goalDiff) return b.goalDiff - a.goalDiff;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  return numericId(a.playerId) - numericId(b.playerId);
}

function emptyFootballStanding(playerId: PlayerId): FootballStanding {
  return {
    playerId,
    matchesPlayed: 0,
    matchesWon: 0,
    matchesDrawn: 0,
    matchesLost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDiff: 0,
    points: 0,
    rank: 0,
  };
}

/** Whether two rows are level on every FIFA criterion before head-to-head. */
function levelOnAllCriteria(a: FootballStanding, b: FootballStanding): boolean {
  return (
    a.points === b.points &&
    a.goalDiff === b.goalDiff &&
    a.goalsFor === b.goalsFor
  );
}

/**
 * Computes ranked FIFA standings for a pool. Only COMPLETED cells contribute;
 * a win is 3 points, a draw 1 each. Ranking: Points -> Goal difference ->
 * Goals scored -> head-to-head result (when exactly two teams are level on all
 * three) -> team number. Ties among three or more teams that survive the first
 * three criteria fall straight to the deterministic team number, standing in
 * for FIFA's drawing of lots.
 */
export function computeFootballStandings(
  playerIds: readonly PlayerId[],
  cells: readonly Cell[],
): FootballStanding[] {
  const table = new Map<PlayerId, FootballStanding>();
  for (const pid of playerIds) {
    table.set(pid, emptyFootballStanding(pid));
  }

  for (const cell of cells) {
    if (cell.status !== 'COMPLETED') continue;
    const a = table.get(cell.playerA);
    const b = table.get(cell.playerB);
    if (!a || !b) continue;

    const summary = summarizeFootballMatch(cell.games);
    if (!summary.played) continue;

    a.matchesPlayed += 1;
    b.matchesPlayed += 1;
    a.goalsFor += summary.goalsA;
    a.goalsAgainst += summary.goalsB;
    b.goalsFor += summary.goalsB;
    b.goalsAgainst += summary.goalsA;

    if (summary.drawn) {
      a.matchesDrawn += 1;
      b.matchesDrawn += 1;
    } else if (summary.winner === 'A') {
      a.matchesWon += 1;
      b.matchesLost += 1;
    } else {
      b.matchesWon += 1;
      a.matchesLost += 1;
    }
  }

  const standings = [...table.values()];
  for (const s of standings) {
    s.goalDiff = s.goalsFor - s.goalsAgainst;
    s.points =
      s.matchesWon * FOOTBALL_WIN_POINTS + s.matchesDrawn * FOOTBALL_DRAW_POINTS;
  }
  standings.sort(compareFootballStandings);

  // Head-to-head pass: when exactly two adjacent teams are level on points,
  // goal difference and goals scored, their mutual completed result decides.
  for (let i = 0; i + 1 < standings.length; i++) {
    const upper = standings[i];
    const lower = standings[i + 1];
    if (!levelOnAllCriteria(upper, lower)) continue;
    // A run of three or more level teams keeps the deterministic order.
    if (i > 0 && levelOnAllCriteria(standings[i - 1], upper)) continue;
    if (i + 2 < standings.length && levelOnAllCriteria(lower, standings[i + 2])) {
      continue;
    }
    const mutual = cells.find(
      (cell) =>
        cell.status === 'COMPLETED' &&
        ((cell.playerA === upper.playerId && cell.playerB === lower.playerId) ||
          (cell.playerA === lower.playerId && cell.playerB === upper.playerId)),
    );
    if (!mutual) continue;
    const summary = summarizeFootballMatch(mutual.games);
    if (summary.winner === null) continue;
    const mutualWinner =
      summary.winner === 'A' ? mutual.playerA : mutual.playerB;
    if (mutualWinner === lower.playerId) {
      standings[i] = lower;
      standings[i + 1] = upper;
    }
  }

  standings.forEach((s, index) => {
    s.rank = index + 1;
  });
  return standings;
}

/**
 * Normalize a manually-entered squad member list: trim entries, drop blanks,
 * cap the length at `TEAM_MEMBER_MAX`. The result is what `Player.members`
 * stores and what the referee form prints.
 */
export function normalizeTeamMembers(
  members: readonly string[] | undefined,
): string[] {
  if (!members) return [];
  return members
    .map((member) => member.trim())
    .filter((member) => member.length > 0)
    .slice(0, TEAM_MEMBER_MAX);
}

/** Stable code identifying why a match's event list is invalid. */
export type FootballEventsInvalidCode =
  | 'EVENTS_WITHOUT_SCORE'
  | 'BAD_MINUTE'
  | 'UNKNOWN_MEMBER'
  | 'TOO_MANY_GOAL_EVENTS'
  | 'TOO_MANY_CARDS';

export interface FootballEventsValidation {
  valid: boolean;
  /** English description of the violation (server responses, logs). */
  reason?: string;
  /** Machine-readable violation code; the UI renders it via the catalogue. */
  code?: FootballEventsInvalidCode;
}

function isSide(value: unknown): value is 'A' | 'B' {
  return value === 'A' || value === 'B';
}

function isMemberIndex(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isMinute(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= 1 &&
      value <= FOOTBALL_MINUTE_MAX)
  );
}

/**
 * Structurally narrows an untrusted `events` payload (a request body) into a
 * `FootballMatchEvent[]`, or null when anything about its shape is off. Only
 * shape is checked here; the semantic rules (roster bounds, goal counts, card
 * caps) live in `validateFootballEvents` so they can also judge already-typed
 * lists against a changed score.
 */
export function parseFootballEvents(
  value: unknown,
): FootballMatchEvent[] | null {
  if (!Array.isArray(value)) return null;
  const events: FootballMatchEvent[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) return null;
    const raw = entry as Record<string, unknown>;
    if (!isSide(raw.side) || !isMemberIndex(raw.memberIndex)) return null;
    const minute = raw.minute === undefined ? null : raw.minute;
    if (!isMinute(minute)) return null;
    if (raw.kind === 'GOAL') {
      if (typeof raw.ownGoal !== 'boolean') return null;
      events.push({
        kind: 'GOAL',
        side: raw.side,
        memberIndex: raw.memberIndex,
        ownGoal: raw.ownGoal,
        minute,
      });
    } else if (raw.kind === 'CARD') {
      if (raw.card !== 'YELLOW' && raw.card !== 'RED') return null;
      events.push({
        kind: 'CARD',
        side: raw.side,
        memberIndex: raw.memberIndex,
        card: raw.card,
        minute,
      });
    } else {
      return null;
    }
  }
  return events;
}

/** The roster a goal's scorer is drawn from: their own side, or the opponent's for an own goal. */
function scorerRosterSide(event: FootballGoalEvent): 'A' | 'B' {
  if (!event.ownGoal) return event.side;
  return event.side === 'A' ? 'B' : 'A';
}

/**
 * Enforces the semantic rules tying a match's events to its score and rosters:
 * events require a recorded score entry; minutes stay in range; every member
 * index falls inside the referenced roster (the opposite side's for an own
 * goal); a side never has more attributed goal events than recorded full-time
 * goals; and one member collects at most `FOOTBALL_YELLOW_MAX` yellows and
 * `FOOTBALL_RED_MAX` reds. Shoot-out conversions are not events, so `games[1]`
 * never enters the arithmetic.
 */
export function validateFootballEvents(
  events: readonly FootballMatchEvent[],
  games: readonly GameScore[],
  rosterSizeA: number,
  rosterSizeB: number,
): FootballEventsValidation {
  if (events.length === 0) return { valid: true };
  if (games.length === 0) {
    return {
      valid: false,
      reason: 'Events can only be recorded once a score is recorded.',
      code: 'EVENTS_WITHOUT_SCORE',
    };
  }

  const rosterSize = (side: 'A' | 'B') =>
    side === 'A' ? rosterSizeA : rosterSizeB;
  const goalCount = { A: 0, B: 0 };
  const cardCounts = new Map<string, { yellows: number; reds: number }>();

  for (const event of events) {
    if (!isMinute(event.minute)) {
      return {
        valid: false,
        reason: `An event minute must be a whole number between 1 and ${FOOTBALL_MINUTE_MAX}.`,
        code: 'BAD_MINUTE',
      };
    }
    const roster =
      event.kind === 'GOAL' ? scorerRosterSide(event) : event.side;
    if (event.memberIndex >= rosterSize(roster)) {
      return {
        valid: false,
        reason: 'An event references a squad member that does not exist.',
        code: 'UNKNOWN_MEMBER',
      };
    }
    if (event.kind === 'GOAL') {
      goalCount[event.side] += 1;
    } else {
      const key = `${event.side}#${event.memberIndex}`;
      const counts = cardCounts.get(key) ?? { yellows: 0, reds: 0 };
      if (event.card === 'YELLOW') counts.yellows += 1;
      else counts.reds += 1;
      cardCounts.set(key, counts);
      if (
        counts.yellows > FOOTBALL_YELLOW_MAX ||
        counts.reds > FOOTBALL_RED_MAX
      ) {
        return {
          valid: false,
          reason: 'A player cannot collect that many cards in one match.',
          code: 'TOO_MANY_CARDS',
        };
      }
    }
  }

  const fullTime = games[0];
  if (goalCount.A > fullTime.pointsA || goalCount.B > fullTime.pointsB) {
    return {
      valid: false,
      reason: 'A team cannot have more attributed scorers than goals.',
      code: 'TOO_MANY_GOAL_EVENTS',
    };
  }
  return { valid: true };
}

/** An event joined with its squad member's list position and name, for display/print. */
export interface ResolvedFootballEvent {
  event: FootballMatchEvent;
  /** 1-based squad-list position of the referenced member (the printed "#"). */
  memberNumber: number;
  /** The member's name, or '' when the roster no longer holds the entry. */
  memberName: string;
}

/**
 * Resolves each event's member reference against the two rosters, preserving
 * entry order. A goal's scorer comes from the scoring side's roster — or the
 * opponent's for an own goal; a card's player always from its own side.
 */
export function resolveFootballEvents(
  events: readonly FootballMatchEvent[],
  membersA: readonly string[],
  membersB: readonly string[],
): ResolvedFootballEvent[] {
  const roster = (side: 'A' | 'B') => (side === 'A' ? membersA : membersB);
  return events.map((event) => {
    const side =
      event.kind === 'GOAL' ? scorerRosterSide(event) : event.side;
    return {
      event,
      memberNumber: event.memberIndex + 1,
      memberName: roster(side)[event.memberIndex] ?? '',
    };
  });
}

/** Type guard narrowing an event to a goal (for split display lists). */
export function isGoalEvent(
  event: FootballMatchEvent,
): event is FootballGoalEvent {
  return event.kind === 'GOAL';
}

/** Type guard narrowing an event to a card (for split display lists). */
export function isCardEvent(
  event: FootballMatchEvent,
): event is FootballCardEvent {
  return event.kind === 'CARD';
}
